import { EngineVersion } from '../../../../aiem/engine/types';
import { getIdFromName } from '../../../../aiem/utils';

export const isMajorUpgrade = (oldVersion = '', newVersion = ''): boolean => {
  if (!oldVersion || !newVersion) return false;

  const parseVersion = (version: string): number[] | null => {
    // Normalize from resource keys and strip suffixes/labels
    const canonical = getIdFromName(version) || version;
    const numeric = stripSuffix(canonical);
    const parts = numeric.split('.').map(Number);
    return parts.some(isNaN) ? null : parts;
  };

  const oldParts = parseVersion(oldVersion);
  const newParts = parseVersion(newVersion);
  if (!oldParts || !newParts) return false;

  if (oldParts[0] !== newParts[0]) return true;
  if (oldParts[1] !== newParts[1]) return true;
  return false;
};

const stripSuffix = (version: string | undefined): string => {
  // Extract the leading dotted numeric sequence, e.g. "2.3.0" from
  // "2.3.0-pr34492-cvefix", "2.2.1 (latest)", "2.1.0+build", etc.
  const match = (version || '').match(/\d+(?:\.\d+)*/);
  return match ? match[0] : '';
};

// Returns canonical version id from resource key (without path prefix)
const getCanonicalFromKey = (key: string | undefined): string => getIdFromName(key || '') || key || '';

// Returns purely numeric dotted version from any string value
export const toNumericVersion = (value: string | undefined): string => stripSuffix(value);

// Convenience: numeric version directly from a resource key
const getNumericFromKey = (key: string | undefined): string => toNumericVersion(getCanonicalFromKey(key));

// Extract suffix portion (everything after the numeric part) from a raw value
const toSuffix = (value: string | undefined): string => {
  const canonical = getIdFromName(value || '') || value || '';
  const numeric = stripSuffix(canonical);
  return canonical.slice(numeric.length).trim();
};

// Compare two version-like strings considering numeric part first, then suffix
const compareVersionsWithSuffix = (a: string | undefined, b: string | undefined): number => {
  const na = toNumericVersion(a);
  const nb = toNumericVersion(b);
  const numericCmp = compareVersions(na, nb);
  if (numericCmp !== 0) return numericCmp;
  const sa = toSuffix(a);
  const sb = toSuffix(b);
  if (sa < sb) return -1;
  if (sa > sb) return 1;
  return 0;
};

export const compareVersions = (a: string, b: string): number => {
  const pa = stripSuffix(a).split('.').map(Number);
  const pb = stripSuffix(b).split('.').map(Number);
  const len = Math.max(pa.length, pb.length);

  for (let i = 0; i < len; i++) {
    const va = pa[i] ?? 0;
    const vb = pb[i] ?? 0;
    if (va < vb) return -1;
    if (va > vb) return 1;
  }
  return 0;
};

export const isVersionInRange = (version: string, min: string, max: string): boolean => {
  const base = stripSuffix(version);
  return compareVersions(base, min) >= 0 && compareVersions(base, max) < 0;
};

export const getUpgradeableVersions = (versions: EngineVersion[], currentVersion?: string): EngineVersion[] => {
  if (!versions || versions.length === 0) return [];

  // Sort versions in descending order (newest first)
  const sortedVersions = [...versions].sort((a, b) => {
    return compareVersionsWithSuffix(getCanonicalFromKey(b.key), getCanonicalFromKey(a.key));
  });

  // Filter out unsupported versions (between 1.11.0 and 2.0.0)
  // Use canonical key (not display name) to avoid labels like "(latest)"
  const supportedVersions = sortedVersions.filter(({ key = '' }) => {
    return !isVersionInRange(getNumericFromKey(key), '1.11.0', '2.0.0');
  });

  // If no current version, return all supported versions
  if (!currentVersion) return supportedVersions;

  // Filter to only show versions higher than current version
  return supportedVersions.filter(
    ({ key }) => compareVersionsWithSuffix(getCanonicalFromKey(key), getCanonicalFromKey(currentVersion)) > 0
  );
};
